import React, { useState, useEffect } from 'react';
import { subscribeToUserNotifications, markNotificationAsSeen, markAllNotificationsAsSeen } from '../services/notificationService';
import { getChannel } from '../services/channelService';
import './NotificationList.css';

const NotificationList = ({ currentUser, onNotificationClick }) => {
  console.log('NotificationList: Component rendering for user:', currentUser?.email);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser) return;

    console.log('NotificationList: Setting up for user:', currentUser.email);
    setLoading(true);
    setError('');

    const unsubscribe = subscribeToUserNotifications(currentUser.email, (notificationsData) => {
      console.log('NotificationList: Received notifications:', notificationsData);
      // Enhance notifications with channel information
      const enhanceNotifications = async () => {
        try {
          const enhancedNotifications = await Promise.all(
            notificationsData.map(async (notification) => {
              try {
                const channel = await getChannel(notification.channelId);
                return {
                  ...notification,
                  channelName: channel.name
                };
              } catch (error) {
                console.error('Error fetching channel for notification:', error);
                return {
                  ...notification,
                  channelName: 'Unknown Channel'
                };
              }
            })
          );
          setNotifications(enhancedNotifications);
        } catch (error) {
          console.error('Error enhancing notifications:', error);
          setNotifications(notificationsData);
        }
        setLoading(false);
      };

      enhanceNotifications();
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as seen
      await markNotificationAsSeen(notification.id, currentUser.email);
      
      // Call the callback to potentially navigate to the post/reply
      if (onNotificationClick) {
        onNotificationClick(notification);
      }
    } catch (error) {
      console.error('Error marking notification as seen:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsSeen(currentUser.email);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setError('Failed to mark all as read');
    }
  };

  const getUsername = (email) => {
    return email.split('@')[0];
  };

  const getNotificationText = (notification) => {
    const username = getUsername(notification.mentionedByUserEmail);
    if (notification.contentType === 'post') {
      return `${username} mentioned you in a post`;
    } else {
      return `${username} mentioned you in a reply`;
    }
  };

  if (loading) {
    return (
      <div className="notification-list-container">
        <div className="notification-list-header">
          <h3>Notifications</h3>
        </div>
        <div className="loading-notifications">
          <div className="loading-spinner-small"></div>
          <p>Loading notifications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notification-list-container">
        <div className="notification-list-header">
          <h3>Notifications</h3>
        </div>
        <div className="notification-error-message">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="notification-list-container">
      <div className="notification-list-header">
        <h3>Notifications</h3>
        {notifications.length > 0 && (
          <button 
            className="mark-all-read-button"
            onClick={handleMarkAllAsRead}
            title="Mark all as read"
          >
            ✓
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="no-notifications">
          <h4>No new notifications</h4>
          <p>You're all caught up!</p>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map((notification) => (
            <div 
              key={notification.id} 
              className="notification-item"
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="notification-info">
                <div className="notification-avatar">
                  {notification.mentionedByUserEmail?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="notification-content">
                  <div className="notification-text">
                    {getNotificationText(notification)}
                  </div>
                  <div className="notification-meta">
                    <span className="notification-channel">#{notification.channelName}</span>
                    <span className="notification-time">{formatDate(notification.createdAt)}</span>
                  </div>
                </div>
              </div>
              <div className="notification-indicator">
                <div className="unread-dot"></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationList;